/**
 * useFormAutoSave Hook - Auto-saves form data to localStorage
 * Prevents data loss on logout, page refresh, or navigation
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { storage } from "../utils/browser.js";
import { logWarn, logError } from "../shared/utils/logger.js";

/**
 * Custom hook for auto-saving form data
 * @param {Object} formData - Current form data state
 * @param {string} storageKey - Unique key for localStorage (e.g., 'lead-form-draft', 'quote-form-draft')
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 1500)
 * @param {Function} options.shouldSave - Function to determine if data should be saved (default: always true)
 * @param {Function} options.transform - Function to transform data before saving (optional)
 * @returns {Object} { hasDraft, restoreDraft, clearDraft, isSaving }
 */
export const useFormAutoSave = (formData, storageKey, options = {}) => {
  const {
    debounceMs = 1500,
    shouldSave = () => true,
    transform = (data) => data,
  } = options;

  const [hasDraft, setHasDraft] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const debounceTimerRef = useRef(null);
  const lastSavedRef = useRef(null);

  // Check if draft exists on mount
  useEffect(() => {
    const draft = storage.get(storageKey);
    setHasDraft(!!draft);
  }, [storageKey]);

  // Auto-save form data with debouncing
  useEffect(() => {
    // Skip if data hasn't changed
    const currentDataStr = JSON.stringify(formData);
    if (currentDataStr === lastSavedRef.current) {
      return;
    }

    // Check if we should save
    if (!shouldSave(formData)) {
      return;
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set saving indicator
    setIsSaving(true);

    // Debounce the save operation
    debounceTimerRef.current = setTimeout(() => {
      try {
        // Transform data if needed (e.g., handle complex objects)
        const dataToSave = transform(formData);
        const dataStr = JSON.stringify(dataToSave);

        // Save to localStorage
        const saved = storage.set(storageKey, dataStr);

        if (saved) {
          lastSavedRef.current = dataStr;
          setHasDraft(true);
          setIsSaving(false);
        } else {
          logWarn("useFormAutoSave: failed to save draft", { storageKey });
          setIsSaving(false);
        }
      } catch (error) {
        logError(`useFormAutoSave: error saving draft for ${storageKey}`, error);
        setIsSaving(false);
      }
    }, debounceMs);

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [formData, storageKey, debounceMs, shouldSave, transform]);

  // Restore draft from localStorage
  const restoreDraft = useCallback(() => {
    try {
      const draftStr = storage.get(storageKey);
      if (!draftStr) {
        return null;
      }

      const draft = JSON.parse(draftStr);
      setHasDraft(false); // Clear flag after restoration
      return draft;
    } catch (error) {
      console.error(`Error restoring draft for ${storageKey}:`, error);
      // Clear corrupted draft
      storage.remove(storageKey);
      return null;
    }
  }, [storageKey]);

  // Clear draft from localStorage
  const clearDraft = useCallback(() => {
    storage.remove(storageKey);
    setHasDraft(false);
    lastSavedRef.current = null;
  }, [storageKey]);

  return {
    hasDraft,
    restoreDraft,
    clearDraft,
    isSaving,
  };
};
